<template>
  <!-- Customer Live Support: a "Support" button (online status, unread badge and preview) that opens the chat.
       Customers can attach one or more of their proxies to a message so support sees which one they mean. -->
  <div class="sw" :class="{ 'is-open': chatOpen }">
    <!-- closed: the button, and a preview of an unread reply -->
    <template v-if="!chatOpen">
      <div v-if="showPreview" class="sw-preview" role="status">
        <button type="button" class="sw-preview__x" :aria-label="t('chat.dismiss')" @click="dismissPreview">×</button>
        <button type="button" class="sw-preview__body" @click="changeChatState">
          <b>{{ t('chat.supportTeam') }}</b>
          <span>{{ previewText }}</span>
        </button>
      </div>
      <button type="button" class="sw-launch" :class="{ 'has-unread': unreadCount > 0 }" :aria-label="t('chat.openChat')" @click="changeChatState">
        <span class="sw-launch__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>
          <i class="sw-dot" :class="{ 'is-off': !online }"></i>
        </span>
        <span class="sw-launch__txt">{{ t('chat.support') }}<small>{{ unreadCount > 0 ? t(unreadCount === 1 ? 'chat.newReply' : 'chat.newReplies', { n: unreadCount }) : (online ? t('chat.onlineNow') : (supportMode === 'offline' ? t('chat.offlineNow') : t('chat.backAt', { time: openTime }))) }}</small></span>
        <span v-if="unreadCount > 0" class="sw-badge">{{ unreadCount }}</span>
      </button>
    </template>

    <!-- open: the chat window -->
    <section v-else class="sw-panel" role="dialog" :aria-label="t('chat.title')" @keydown.esc="changeChatState" @dragover.prevent="dragging = true" @dragleave.self="dragging = false" @drop.prevent="onDrop">
      <header class="sw-head">
        <button type="button" class="sw-icon sw-back" :aria-label="t('chat.close')" @click="changeChatState">‹</button>
        <span class="sw-mark" aria-hidden="true">PP</span>
        <div class="sw-who">
          <b>{{ t('chat.title') }}</b>
          <small><i class="sw-dot sw-dot--inline" :class="{ 'is-off': !online }"></i>{{ online ? t('chat.headerOnline') : (supportMode === 'offline' ? t('chat.headerOfflineManual') : t('chat.headerOfflineAt', { time: openTime })) }}</small>
        </div>
        <a v-if="tgUrl" class="sw-icon" :href="tgUrl" target="_blank" rel="noopener noreferrer" title="Telegram" aria-label="Telegram">
          <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21.9 4.3 18.7 19.4c-.2 1.1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-5 9.1-8.2c.4-.4-.1-.6-.6-.2L6.2 13 1.4 11.5c-1-.3-1.1-1 .2-1.5L20.5 2.8c.9-.3 1.6.2 1.4 1.5z" /></svg>
        </a>
        <button type="button" class="sw-icon sw-min" :aria-label="t('chat.close')" @click="changeChatState">—</button>
      </header>

      <div ref="chatContent" class="sw-body" @scroll="handleScroll">
        <!-- the customer's server is in an outage ProxyMonitor confirmed: say so before they write -->
        <div v-for="o in knownOutages" :key="o.server" class="sw-known" role="status"><b>{{ t('outage.knownIssue') }}</b> {{ t('outage.knownText', { server: o.server, time: new Date(o.since).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }) }}</div>
        <!-- first visit: greeting, topics, help-centre answers -->
        <div v-if="!messageList.length && !loadingMessages" class="sw-welcome">
          <h4>{{ t('chat.greeting', { name: firstName }) }}</h4>
          <p>{{ t('chat.greetingSub', { from: openTime, to: closeTime }) }}</p>
          <div v-if="!online" class="sw-offline">{{ t('chat.offlineNote') }}</div>
          <div class="sw-topics">
            <button v-for="tp in TOPICS" :key="tp.key" type="button" class="sw-topic" @click="pickTopic(tp)">{{ t(tp.label) }}</button>
          </div>
          <template v-if="helpLinks.length">
            <p class="sw-sub">{{ t('chat.quickAnswers') }}</p>
            <router-link v-for="f in helpLinks" :key="f.id" class="sw-article" :to="{ path: '/faq', hash: '#faq-' + f.id }" @click="changeChatState">
              <span>{{ f.question }}</span><em>{{ t('chat.read') }}</em>
            </router-link>
          </template>
        </div>

        <template v-for="row in rows" :key="row.key">
          <span v-if="row.kind === 'day'" class="sw-day">{{ row.label }}</span>
          <span v-else-if="row.kind === 'sender'" class="sw-sender">{{ t('chat.supportLabel') }}</span>
          <span v-else-if="row.kind === 'seen'" class="sw-seen">{{ t('chat.seen') }}</span>
          <div v-else class="sw-msg" :class="[row.mine ? 'is-me' : 'is-them', { 'has-picker': pickerFor === row.m.id }]" @click="pickerFor = null">
            <div v-if="row.m.replyTo" class="sw-quote"><b>{{ sameUserId(row.m.replyTo.from, user?.id) ? t('chat.you') : t('chat.supportLabel') }}</b><span>{{ replySnippet(row.m.replyTo) }}</span></div>
            <p v-if="row.m.deleted" class="sw-text sw-deleted">{{ t('chat.deleted') }}</p>
            <a v-if="row.m.image_url" :href="row.m.image_url" class="sw-img" @click.prevent="lightbox = row.m.image_url"><img :src="row.m.image_url" alt="picture" loading="lazy" /></a>
            <a v-if="row.m.file_url" :href="row.m.file_url" class="sw-file" :download="row.m.file_name || 'file'"><v-icon icon="mdi-file-download-outline" size="16"></v-icon> {{ row.m.file_name || 'file' }}</a>
            <div v-for="(card, ci) in row.cards" :key="'c' + ci" class="sw-proxy">🔗 {{ card }}</div>
            <p v-if="row.text" class="sw-text"><template v-for="(part, pi) in splitLinks(row.text)" :key="pi"><a v-if="part.link" :href="part.value" target="_blank" rel="noopener noreferrer nofollow">{{ part.value }}</a><template v-else>{{ part.value }}</template></template></p>
            <span class="sw-time">{{ timeOf(row.m.timestamp) }}<i v-if="row.m.edited && !row.m.deleted"> · {{ t('chat.edited') }}</i></span>
            <button v-if="!row.m.deleted" type="button" class="sw-react-btn" :aria-label="t('chat.react')" :title="t('chat.react')" @click.stop="pickerFor = pickerFor === row.m.id ? null : row.m.id">☺</button>
            <div v-if="pickerFor === row.m.id" class="sw-picker-pop" @click.stop>
              <button v-for="e in REACTION_EMOJI" :key="e" type="button" @click="react(row.m, e)">{{ e }}</button>
            </div>
            <div v-if="row.m.reactions && row.m.reactions.length" class="sw-reactions">
              <button v-for="r in row.m.reactions" :key="r.emoji" type="button" class="sw-chip-react" :class="{ 'is-mine': r.mine }" :title="reactionTitle(r)" @click.stop="react(row.m, r.emoji)">{{ r.emoji }}<small v-if="r.count > 1">{{ r.count }}</small><em v-if="reactionLabel(r.emoji)">{{ t(r.emoji === '👀' ? 'chat.statusLooking' : 'chat.statusDone') }}</em></button>
            </div>
          </div>
        </template>

        <!-- Anti-detect advice: our partner's guide (shown here only, nothing is sent) -->
        <div v-if="advice" class="sw-advice">
          <button type="button" class="sw-advice__x" :aria-label="t('chat.dismiss')" @click="advice = false">×</button>
          <div class="sw-advice__top"><span class="sw-partner-mark" aria-hidden="true">U</span><span><b>{{ t('chat.adviceTitle') }}</b><span class="sw-partner-tag">{{ t('chat.recommendedPartner') }}</span></span></div>
          <p>{{ t('chat.adviceText') }}</p>
          <div class="sw-advice__btns">
            <a class="sw-btn sw-advice__go" :href="UNDETECTABLE.url" target="_blank" rel="sponsored noopener">{{ t('chat.adviceTry') }} ↗</a>
            <a class="sw-link-btn" :href="UNDETECTABLE.guideUrl" target="_blank" rel="sponsored noopener">{{ t('chat.adviceLink') }} ↗</a>
          </div>
          <span class="sw-sub">{{ t('chat.adviceAsk') }}</span>
        </div>
      </div>

      <!-- picking proxies to attach -->
      <div v-if="picker" class="sw-picker">
        <div class="sw-picker__head">
          <b>{{ picker.title }}</b>
          <button type="button" class="sw-link" @click="picker = null">{{ t('chat.cancel') }}</button>
        </div>
        <p v-if="proxiesLoading" class="sw-sub">{{ t('chat.loadingProxies') }}</p>
        <p v-else-if="!myProxies.length" class="sw-sub">{{ t('chat.noProxies') }}</p>
        <div v-else class="sw-picker__list">
          <label v-for="p in myProxies" :key="p.key" class="sw-pick" :class="{ 'is-on': picked.includes(p.key) }">
            <input v-model="picked" type="checkbox" :value="p.key" />
            <span><b>{{ p.server }}</b><small>{{ p.ports }}<template v-if="p.expired"> · {{ t('chat.expired') }}</template></small></span>
          </label>
        </div>
        <div class="sw-picker__foot">
          <button v-if="picker.optional || (!proxiesLoading && !myProxies.length)" type="button" class="sw-link" @click="finishPick([])">{{ myProxies.length ? t('chat.notSpecific') : t('chat.continue') }}</button>
          <button v-if="myProxies.length" type="button" class="sw-btn" :disabled="!picked.length" @click="finishPick(picked)">{{ t('chat.attachN', { n: picked.length }) }}</button>
        </div>
      </div>

      <div v-if="attached.length" class="sw-attached">
        <span v-for="a in attached" :key="a.key" class="sw-chip">🔗 {{ a.label }}<button type="button" :aria-label="t('chat.remove')" @click="attached = attached.filter((x) => x.key !== a.key)">×</button></span>
      </div>
      <div v-if="pendingImage" class="sw-pending">
        <img v-if="pendingPreview" :src="pendingPreview" alt="" />
        <span v-else class="sw-file"><v-icon icon="mdi-file-outline" size="16"></v-icon> {{ pendingImage.name }} ({{ fileSize(pendingImage.size) }})</span>
        <span class="sw-sub">{{ pendingPreview ? t('chat.pictureReady') : t('chat.fileReady') }}</span>
        <button type="button" class="sw-link" @click="clearPending">{{ t('chat.remove') }}</button>
      </div>

      <div v-if="messageList.length && !messageInput.trim() && !attached.length && !picker && !pendingImage" class="sw-quick">
        <button v-for="tp in TOPICS" :key="tp.key" type="button" class="sw-topic sw-topic--small" @click="pickTopic(tp)">{{ t(tp.label) }}</button>
      </div>
      <div v-if="menuOpen" class="sw-menu" @click.stop>
        <button type="button" @click="openPicker(null)">🔗 {{ t('chat.attachProxy') }}<small>{{ t('chat.attachProxySub') }}</small></button>
        <button type="button" @click="menuOpen = false; fileInput.click()">🖼 {{ t('chat.pictureOrFile') }}<small>{{ t('chat.pictureOrFileSub') }}</small></button>
      </div>
      <div class="sw-compose">
        <button type="button" class="sw-tool" :aria-label="t('chat.attach')" :aria-expanded="menuOpen" @click.stop="menuOpen = !menuOpen">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
        </button>
        <textarea ref="input" v-model="messageInput" rows="1" class="sw-input" :placeholder="t('chat.placeholder')" @keydown.enter.exact.prevent="send" @input="grow" @paste="onPaste"></textarea>
        <button type="button" class="sw-send" :aria-label="t('chat.send')" :disabled="sending" @click="send">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </div>
      <div class="sw-hint">{{ t('chat.enterHint') }}</div>
      <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/gif,image/webp,.txt,.ovpn,.conf,.log,.csv,.json,.pdf" class="hidden" @change="onPickImage" />
      <div v-if="dragging" class="sw-drop">{{ t('chat.dropHere') }}</div>
    </section>

    <!-- picture viewer: click anywhere or press Esc to close -->
    <Teleport to="body">
      <div v-if="lightbox" ref="lightboxEl" class="chat-lightbox" tabindex="0" @click="lightbox = ''" @keydown.esc="lightbox = ''">
        <img :src="lightbox" alt="picture" @click.stop />
        <button type="button" class="chat-lightbox-close" aria-label="Close">&times;</button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { useAccountStore, useAlertStore, useFAQStore } from "@/stores"
import { useChatStore } from "@/stores/chat.store";
import { storeToRefs } from "pinia"
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { UNDETECTABLE } from '@/helpers/partners'   // recommended anti-detect partner (affiliate link)
import { useRoute } from 'vue-router'
import { fetchWrapper } from '@/helpers'
import { t, locale } from '@/i18n'
import { hourLabel } from '@/helpers/support-hours'
import { quickLinkOf } from '@/helpers/faq-format'
import { splitLinks } from '@/utils/chat-text'
import { REACTION_EMOJI, reactionLabel, reactionTitle, replySnippet } from '@/utils/chat-reactions'

const accountStore = useAccountStore()
const alertStore = useAlertStore();
const { user, tglink } = storeToRefs(accountStore)
const route = useRoute()
const PUBLIC_PAGES = ['/login', '/register', '/forgotpass', '/reset']
const chatStore = useChatStore();
const { messages, unreadCount, loadingMessages } = storeToRefs(chatStore);
const chatOpen = ref(false)
const messageInput = ref('')
const chatContent = ref(null);
const input = ref(null)
const sending = ref(false)
const menuOpen = ref(false)
const dragging = ref(false)
let chatPoller = null;
let unreadPoller = null;
let clockTimer = null;

const messageList = computed(() => messages.value || []);
// emoji reactions: a small ☺ on each bubble opens the palette; a chip toggles that emoji
const pickerFor = ref(null);
const react = async (m, emoji) => { pickerFor.value = null; try { await chatStore.react(m.id, emoji); } catch (e) { alertStore.error(e?.message || e); } };
const sameUserId = (left, right) => left !== undefined && left !== null && right !== undefined && right !== null && String(left) === String(right);
const isMine = (message) => sameUserId(message.from, user.value?.id);
const firstName = computed(() => String(user.value?.name || '').trim().split(/\s+/)[0] || '');
const tgUrl = computed(() => { const v = String(tglink.value || '').trim(); if (!v) return ''; return /^https?:\/\//i.test(v) ? v : 'https://t.me/' + v.replace(/^@/, ''); });

// ---- support hours: 9am to 10pm UK time ----
// ---- support status: set by admins on the Messages page (automatic hours, or manually online / offline) ----
const ukHour = () => Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: 'numeric', hourCycle: 'h23' }).format(new Date()));
const support = ref({ mode: 'auto', from: 9, to: 22 });
const supportMode = computed(() => support.value.mode);
const inHours = () => { const s = support.value, h = ukHour(); return s.from < s.to ? h >= s.from && h < s.to : h >= s.from || h < s.to; };
const online = ref(true);
const updateOnline = () => { const m = support.value.mode; online.value = m === 'online' ? true : m === 'offline' ? false : inHours(); };
const loadSupportStatus = async () => {
  try { const s = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/chat/support-status`); if (s && s.mode) support.value = { mode: s.mode, from: s.from, to: s.to }; }
  catch (e) { /* keep the last known status */ }
  updateOnline();
};
const openTime = computed(() => hourLabel(support.value.from, locale.value === 'zh-CN'));
const closeTime = computed(() => hourLabel(support.value.to, locale.value === 'zh-CN'));

// ---- messages: day dividers, "Support" above a run of replies, "Seen" under the customer's last message ----
const PROXY_LINE = /^🔗\s*(.+)$/u;
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const dayLabel = (d) => {
  const today = new Date(); const y = new Date(); y.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(today)) return t('chat.today');
  if (dayKey(d) === dayKey(y)) return t('chat.yesterday');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};
const toDate = (ts) => { const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts); return Number.isNaN(d.getTime()) ? null : d; };
const timeOf = (ts) => { const d = toDate(ts); return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''; };
const rows = computed(() => {
  const out = [];
  let lastDay = '', lastMine = null;
  const list = messageList.value;
  let lastMineIdx = -1;
  list.forEach((m, i) => { if (isMine(m)) lastMineIdx = i; });
  const seen = lastMineIdx >= 0 && (list[lastMineIdx].read || list.slice(lastMineIdx + 1).some((m) => !isMine(m)));
  list.forEach((m, i) => {
    const d = toDate(m.timestamp);
    const k = d ? dayKey(d) : '';
    if (d && k !== lastDay) { out.push({ kind: 'day', key: 'd' + i, label: dayLabel(d) }); lastDay = k; lastMine = null; }
    const mine = isMine(m);
    if (!mine && lastMine !== false) out.push({ kind: 'sender', key: 's' + i });
    lastMine = mine;
    const lines = String(m.text || '').split('\n');
    const cards = lines.filter((l) => PROXY_LINE.test(l.trim())).map((l) => l.trim().match(PROXY_LINE)[1]);
    const text = lines.filter((l) => !PROXY_LINE.test(l.trim())).join('\n').trim();
    out.push({ kind: 'msg', key: 'm' + (m.id ?? i), m, mine, cards, text });
    if (i === lastMineIdx && seen) out.push({ kind: 'seen', key: 'seen' });
  });
  return out;
});

// ---- unread preview on the closed button ----
const dismissedId = ref(null);
const lastUnread = computed(() => [...messageList.value].reverse().find((m) => !isMine(m) && !m.read) || null);
const previewText = computed(() => {
  const m = lastUnread.value; if (!m) return '';
  const text = String(m.text || '').replace(/\s+/g, ' ').trim();
  return text ? (text.length > 110 ? text.slice(0, 107) + '…' : text) : (m.image_url ? t('chat.sentPicture') : t('chat.sentFile'));
});
const showPreview = computed(() => unreadCount.value > 0 && lastUnread.value && lastUnread.value.id !== dismissedId.value && !PUBLIC_PAGES.includes(route.path));
const dismissPreview = () => { dismissedId.value = lastUnread.value?.id ?? null; };

// ---- help centre answers for a first visit ----
const faqStore = useFAQStore();
const helpLinks = computed(() => (faqStore.faqs || []).filter((f) => !quickLinkOf(f)).slice(0, 3));

// ---- attach proxies ----
const TOPICS = [
  { key: 'down', label: 'chat.topicDown', pick: true },
  { key: 'rotation', label: 'chat.topicRotation', pick: true },
  { key: 'billing', label: 'chat.topicBilling' },
  { key: 'antidetect', label: 'chat.topicAntidetect', link: true },
];
const advice = ref(false);
const myProxies = ref([]);
const proxiesLoading = ref(false);
const picker = ref(null);        // { title, optional, topic }
const picked = ref([]);
const attached = ref([]);        // [{ key, label }]
const loadProxies = async () => {
  proxiesLoading.value = true;
  try {
    const res = await fetchWrapper.post(`${process.env.VUE_APP_VUE_API_URL}/proxy/my-list`);
    myProxies.value = (res?.proxyList || []).map((p) => {
      const ports = `${p.http_port}${p.socket_port && String(p.socket_port) !== String(p.http_port) ? ' / ' + p.socket_port : ''}`;
      const expired = String(p.status || '').toUpperCase() !== 'ACTIVE' || (p.expired_date && new Date(p.expired_date).getTime() < Date.now());
      return { key: String(p.uuid || p.id || p.modem_name + ports), server: p.modem_name || '-', ports, expired, label: `${p.modem_name} · ${ports}` };
    }).sort((a, b) => Number(a.expired) - Number(b.expired) || a.server.localeCompare(b.server));
  } catch (e) { myProxies.value = []; }
  proxiesLoading.value = false;
};
const openPicker = async (topic) => {
  menuOpen.value = false;
  picked.value = attached.value.map((a) => a.key);
  picker.value = { title: topic ? t('chat.whichProxy') : t('chat.attachProxy'), optional: Boolean(topic?.optional), topic };
  if (!myProxies.value.length) await loadProxies();
  if (myProxies.value.length === 1 && !picked.value.length) picked.value = [myProxies.value[0].key];
};
const finishPick = (keys) => {
  attached.value = myProxies.value.filter((p) => keys.includes(p.key)).map((p) => ({ key: p.key, label: p.label }));
  const topic = picker.value?.topic;
  picker.value = null;
  if (topic && !messageInput.value.trim()) messageInput.value = t(topic.label) + ': ';
  nextTick(() => { input.value?.focus(); grow(); });
};
const pickTopic = (tp) => {
  if (tp.link) { advice.value = true; nextTick(() => { if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight; }); return; }
  if (tp.pick) { openPicker(tp); return; }
  messageInput.value = t(tp.label) + ': ';
  nextTick(() => { input.value?.focus(); grow(); });
};

// ---- composer ----
const grow = () => { const el = input.value; if (!el) return; el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; };
const withAttached = (text) => {
  if (!attached.value.length) return text;
  return attached.value.map((a) => '🔗 ' + a.label).join('\n') + (text ? '\n' + text : '');
};

// pictures and files: + menu, paste or drop (5 MB)
const fileInput = ref(null);
const pendingImage = ref(null);
const pendingPreview = ref("");
const lightbox = ref("");
const lightboxEl = ref(null);
watch(lightbox, (v) => { if (v) nextTick(() => lightboxEl.value && lightboxEl.value.focus()); });
const CHAT_FILE_EXT = ['txt', 'ovpn', 'conf', 'log', 'csv', 'json', 'pdf'];
const isChatFile = (f) => CHAT_FILE_EXT.includes(String(f?.name || '').split('.').pop().toLowerCase());
const fileSize = (n) => n > 1024 * 1024 ? (n / 1024 / 1024).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB';
const setPending = (file) => {
  if (!file || !(file.type.startsWith("image/") || isChatFile(file))) { alertStore.error(t('chat.fileTypes')); return; }
  if (file.size > 5 * 1024 * 1024) { alertStore.error(t('chat.fileSize')); return; }
  clearPending(); pendingImage.value = file; pendingPreview.value = file.type.startsWith("image/") ? URL.createObjectURL(file) : "";
};
const clearPending = () => { if (pendingPreview.value) URL.revokeObjectURL(pendingPreview.value); pendingImage.value = null; pendingPreview.value = ""; };
const onPickImage = (e) => { const f = e.target.files?.[0]; e.target.value = ""; setPending(f); };
const onPaste = (e) => { const item = [...(e.clipboardData?.items || [])].find((i) => i.type.startsWith("image/")); if (item) { e.preventDefault(); setPending(item.getAsFile()); } };
const onDrop = (e) => { dragging.value = false; const f = e.dataTransfer?.files?.[0]; if (f) setPending(f); };

const afterSend = () => {
  loadingMoreMessages = false;
  messageInput.value = '';
  attached.value = [];
  nextTick(() => { grow(); input.value?.focus(); if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight; });
};
const send = async () => {
  if (sending.value) return;
  const text = withAttached(String(messageInput.value || '').trim());
  sending.value = true;
  try {
    if (pendingImage.value) {
      const m = await chatStore.uploadImage(pendingImage.value, null, text);
      if (m) { clearPending(); afterSend(); }
      return;
    }
    if (!text) { alertStore.error(t('chat.enterMessage')); return; }
    await chatStore.sendMessage(text);
    afterSend();
  } catch (error) {
    alertStore.error(error);
  } finally {
    sending.value = false;
  }
};

// ---- open / close ----
// outages on the customer's servers (one line per server)
const knownOutages = ref([]);
const loadKnownOutages = async () => {
  try {
    const r = await fetchWrapper.get(`${process.env.VUE_APP_VUE_API_URL}/outages/mine`);
    const seen = new Set();
    knownOutages.value = Object.values(r.licenses || {}).filter((o) => o.state === 'outage' && !seen.has(o.server) && seen.add(o.server));
  } catch (_) { knownOutages.value = []; }
};
const changeChatState = () => {
  chatOpen.value = !chatOpen.value;
  menuOpen.value = false;
  picker.value = null;
  if (chatOpen.value) {
    loadSupportStatus();
    loadKnownOutages();
    chatStore.getMessages();   // fresh copy, including whether support has read the last message
    if (!faqStore.faqs?.length) faqStore.getAll().catch(() => null);
    nextTick(async () => {
      await chatStore.messageRead();
      if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
      grow();
      input.value?.focus();
    });
  }
};
const openFromPage = () => { if (!chatOpen.value) changeChatState(); };
const closeMenu = () => { menuOpen.value = false; pickerFor.value = null; };

let loadingMoreMessages = false;
const handleScroll = () => {
  if (chatContent.value.scrollTop === 0 && !loadingMoreMessages) {
    const previous = chatContent.value.scrollHeight;
    loadingMoreMessages = true;
    chatStore.loadMoreMessages().then(() => {
      nextTick(() => {
        loadingMoreMessages = false;
        chatContent.value.scrollTop = chatContent.value.scrollHeight - previous;
      });
    });
  }
};

onMounted(async () => {
  window.addEventListener('pp-open-live-support', openFromPage);
  document.addEventListener('click', closeMenu);
  loadSupportStatus();
  clockTimer = setInterval(loadSupportStatus, 60000);   // picks up an admin's switch within a minute
  if (!tglink.value && accountStore.getTgLink) accountStore.getTgLink().catch(() => null);
  await chatStore.getMessages();
  await chatStore.loadUnreadCount();
  // messages refresh every 4 s only while the chat is open; otherwise the unread count is checked every 15 s
  // and the messages are fetched when it goes up. Every 20 s while open, the read marks are refreshed too.
  let tick = 0;
  chatPoller = setInterval(async () => {
    if (!chatOpen.value || !user.value?.accessToken) return;
    tick++;
    if (tick % 5 === 0 && !loadingMoreMessages && chatContent.value && chatContent.value.scrollTop + chatContent.value.clientHeight >= chatContent.value.scrollHeight - 40) await chatStore.getMessages();
    else await chatStore.refreshMessages();
  }, 4000);
  unreadPoller = setInterval(async () => {
    if (chatOpen.value || PUBLIC_PAGES.includes(route.path)) return;
    const before = unreadCount.value;
    const after = await chatStore.loadUnreadCount();
    if (after > before) await chatStore.refreshMessages();
  }, 15000);
});

onBeforeUnmount(() => {
  window.removeEventListener('pp-open-live-support', openFromPage);
  document.removeEventListener('click', closeMenu);
  if (chatPoller) clearInterval(chatPoller);
  if (unreadPoller) clearInterval(unreadPoller);
  if (clockTimer) clearInterval(clockTimer);
});

watch(messages, (list, before) => {
  if (!chatOpen.value) return;
  const grew = (list?.length || 0) !== (before?.length || 0);
  nextTick(async () => {
    if (!loadingMoreMessages && grew) {
      await chatStore.messageRead();
      if (chatContent.value) chatContent.value.scrollTop = chatContent.value.scrollHeight;
    }
  });
});
</script>

<style scoped>
/* ---- button ---- */
.sw { position: fixed; right: 20px; bottom: 20px; z-index: 200; }
.sw.is-open { z-index: 1000; }
.sw-launch { position: relative; display: inline-flex; align-items: center; gap: 14px; height: 73px; padding: 0 25px 0 10px; border: 0; border-radius: 999px; background: var(--ui-pri); color: #ffffff; font-size: 21px; font-weight: 700; cursor: pointer; box-shadow: 0 10px 28px rgba(10, 8, 40, 0.42); transition: transform 150ms ease, background-color 150ms ease; }
.sw-launch:hover, .sw-launch:focus-visible { background: var(--ui-pri-hover); transform: translateY(-2px); outline: none; }
.sw-launch__icon { position: relative; display: grid; place-items: center; width: 53px; height: 53px; border-radius: 50%; background: rgba(255, 255, 255, 0.16); }
.sw-launch__icon svg { width: 28px; height: 28px; }
.sw-launch__txt { text-align: left; line-height: 1.15; }
.sw-launch__txt small { display: block; margin-top: 1px; font-size: 15px; font-weight: 600; opacity: 0.85; }
.sw-dot { position: absolute; right: 1px; bottom: 1px; width: 14px; height: 14px; border: 2px solid var(--ui-pri); border-radius: 50%; background: #22c38e; }
.sw-dot.is-off { background: #9a98b3; }
.sw-dot--inline { position: static; display: inline-block; width: 8px; height: 8px; margin-right: 6px; border: 0; vertical-align: 1px; }
.sw-badge { position: absolute; top: -6px; right: -6px; display: grid; place-items: center; min-width: 25px; height: 25px; padding: 0 6px; border: 2px solid var(--ui-page); border-radius: 999px; background: #e5484d; color: #ffffff; font-size: 13px; font-weight: 800; }
.sw-launch.has-unread { animation: sw-nudge 2.6s ease-in-out 1s 2; }
@keyframes sw-nudge { 0%, 100% { transform: none; } 10% { transform: translateY(-4px); } 20% { transform: none; } 30% { transform: translateY(-2px); } 40% { transform: none; } }
.sw-preview { position: absolute; right: 0; bottom: 85px; width: 260px; border: 1px solid var(--ui-line-strong); border-radius: 12px 12px 4px 12px; background: var(--ui-card); box-shadow: 0 8px 24px rgba(10, 8, 40, 0.35); }
.sw-preview__body { display: block; width: 100%; padding: 10px 26px 10px 12px; border: 0; background: transparent; color: var(--ui-ink); font-size: 13px; text-align: left; cursor: pointer; }
.sw-preview__body b { display: block; margin-bottom: 2px; font-size: 12px; color: var(--ui-teal); }
.sw-preview__x { position: absolute; top: 4px; right: 6px; border: 0; background: transparent; color: var(--ui-dim); font-size: 16px; cursor: pointer; }

/* ---- window ---- */
.sw-panel { position: relative; display: flex; flex-direction: column; width: 380px; height: min(600px, calc(100vh - 40px)); overflow: hidden; border: 1px solid var(--ui-line-strong); border-radius: 16px; background: var(--ui-card); color: var(--ui-ink); box-shadow: 0 18px 50px rgba(8, 6, 30, 0.5); }
.sw-head { display: flex; align-items: center; gap: 10px; padding: 12px 12px 12px 14px; background: linear-gradient(135deg, #5a55e8, #4b47c9); color: #ffffff; }
.sw-mark { display: grid; place-items: center; flex: none; width: 34px; height: 34px; border-radius: 50%; background: #ffffff; color: #4b47c9; font-size: 13px; font-weight: 800; }
.sw-who { flex: 1; min-width: 0; }
.sw-who b { display: block; font-size: 15px; }
.sw-who small { display: block; font-size: 12px; opacity: 0.92; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sw-icon { display: grid; place-items: center; flex: none; width: 32px; height: 32px; border: 0; border-radius: 8px; background: rgba(255, 255, 255, 0.14); color: #ffffff !important; font-size: 16px; text-decoration: none; cursor: pointer; }
.sw-icon:hover { background: rgba(255, 255, 255, 0.26); }
.sw-icon svg { width: 16px; height: 16px; }
.sw-back { display: none; font-size: 24px; }
.sw-body { flex: 1; display: flex; flex-direction: column; gap: 4px; overflow-y: auto; padding: 12px 12px 6px; background: var(--ui-page); }
.sw-day { align-self: center; margin: 8px 0 6px; padding: 2px 10px; border-radius: 999px; background: var(--ui-card); color: var(--ui-dim); font-size: 11.5px; font-weight: 600; }
.sw-sender { align-self: flex-start; margin: 6px 0 0 4px; font-size: 11.5px; font-weight: 700; color: var(--ui-teal); }
.sw-seen { align-self: flex-end; margin: 1px 4px 4px; font-size: 11px; color: var(--ui-dim); }
.sw-msg { max-width: 84%; padding: 8px 12px; border-radius: 16px; font-size: 14px; line-height: 1.4; overflow-wrap: anywhere; }
.sw-msg.is-them { align-self: flex-start; border-bottom-left-radius: 4px; background: var(--ui-card-2); color: var(--ui-ink); }
.sw-msg.is-me { align-self: flex-end; border-bottom-right-radius: 4px; background: var(--ui-pri); color: #ffffff; }
.sw-text { margin: 0; white-space: pre-wrap; }
.sw-time { display: block; margin-top: 3px; font-size: 11px; text-align: right; opacity: 0.7; }
.sw-proxy { margin: 2px 0 6px; padding: 6px 9px; border-radius: 9px; background: rgba(255, 255, 255, 0.16); font-size: 12.5px; font-weight: 700; }
.sw-msg.is-them .sw-proxy { background: var(--ui-pri-soft); }
.sw-img img { display: block; max-width: 100%; max-height: 220px; margin-bottom: 6px; border-radius: 10px; cursor: zoom-in; }
.sw-file { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 6px; padding: 5px 9px; border: 1px solid rgba(128, 128, 128, 0.4); border-radius: 8px; color: inherit; font-size: 12px; text-decoration: none; }
.sw-welcome { display: grid; gap: 10px; padding: 4px 2px; }
.sw-welcome h4 { margin: 4px 0 0; font-size: 16px; font-weight: 700; color: var(--ui-ink); }
.sw-welcome p { margin: 0; color: var(--ui-dim); font-size: 13.5px; }
.sw-sub { margin: 4px 0 0; color: var(--ui-dim); font-size: 12.5px; }
.sw-offline { padding: 9px 11px; border-radius: 10px; background: var(--ui-warn-soft); color: var(--ui-warn-ink); font-size: 12.5px; }
.sw-topics { display: flex; flex-wrap: wrap; gap: 6px; }
.sw-topic { padding: 7px 12px; border: 1px solid var(--ui-line-strong); border-radius: 999px; background: var(--ui-card); color: var(--ui-ink); font-size: 13px; font-weight: 600; cursor: pointer; }
.sw-topic:hover, .sw-topic:focus-visible { border-color: var(--ui-pri); outline: none; }
.sw-article { display: flex; align-items: center; gap: 10px; padding: 9px 11px; border: 1px solid var(--ui-line); border-radius: 10px; background: var(--ui-card); color: var(--ui-ink) !important; font-size: 13px; text-decoration: none; }
.sw-article span { flex: 1; }
.sw-article em { font-style: normal; font-size: 12px; font-weight: 700; color: var(--ui-teal); }
.sw-article:hover { border-color: var(--ui-pri); }

.sw-picker { display: flex; flex-direction: column; gap: 8px; max-height: 60%; margin: 0 10px 8px; padding: 10px; border: 1px solid var(--ui-line-strong); border-radius: 12px; background: var(--ui-card); }
.sw-picker__head { display: flex; align-items: center; justify-content: space-between; font-size: 14px; }
.sw-picker__list { display: grid; gap: 4px; overflow-y: auto; }
.sw-pick { display: flex; align-items: center; gap: 10px; margin: 0; padding: 7px 9px; border: 1px solid var(--ui-line); border-radius: 9px; background: var(--ui-page); cursor: pointer; }
.sw-pick.is-on { border-color: var(--ui-pri); background: var(--ui-pri-soft); }
.sw-pick input { accent-color: var(--ui-pri); }
.sw-pick b { display: block; font-size: 13px; }
.sw-pick small { font-size: 12px; color: var(--ui-dim); }
.sw-picker__foot { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.sw-btn { padding: 7px 14px; border: 0; border-radius: 8px; background: var(--ui-pri); color: #ffffff; font-size: 13px; font-weight: 700; cursor: pointer; }
.sw-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sw-link { border: 0; background: transparent; color: var(--ui-dim); font-size: 12.5px; font-weight: 600; text-decoration: underline; cursor: pointer; }
.sw-quick { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 12px 0; }   /* wraps, so every topic is visible */
.sw-topic--small { padding: 4px 10px; font-size: 12px; }
.sw-advice { position: relative; display: grid; gap: 6px; margin: 8px 0 4px; padding: 12px 14px; border: 1px solid var(--ui-teal); border-radius: 12px; background: var(--ui-card); }
.sw-advice b { font-size: 14px; }
.sw-advice p { margin: 0; font-size: 13px; color: var(--ui-ink); }
.sw-advice__go { justify-self: start; color: #0b1b16 !important; background: #2ec4a0; text-decoration: none; }
.sw-advice__top { display: flex; align-items: center; gap: 10px; padding-right: 16px; }
.sw-advice__top b { display: block; }
.sw-advice__btns { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.sw-partner-mark { display: grid; place-items: center; flex: none; width: 34px; height: 34px; border-radius: 9px; background: #0f1720; color: #2ec4a0; font-weight: 800; }
.sw-partner-tag { display: inline-block; margin-top: 2px; padding: 0 7px; border-radius: 999px; background: rgba(46, 196, 160, 0.16); color: #7fe3c8; font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.sw-link-btn { padding: 6px 10px; border: 1px solid var(--ui-line-strong); border-radius: 8px; color: var(--ui-ink) !important; font-size: 12.5px; font-weight: 600; text-decoration: none; }
.sw-link-btn:hover { border-color: var(--ui-pri); }
.sw-advice__x { position: absolute; top: 6px; right: 8px; border: 0; background: transparent; color: var(--ui-dim); font-size: 16px; cursor: pointer; }
.sw-attached { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 12px 0; }
.sw-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 6px 4px 10px; border-radius: 999px; background: var(--ui-pri-soft); color: var(--ui-ink); font-size: 12px; font-weight: 600; }
.sw-chip button { border: 0; background: transparent; color: var(--ui-dim); font-size: 14px; cursor: pointer; }
.sw-pending { display: flex; align-items: center; gap: 8px; padding: 8px 12px 0; }
.sw-pending img { width: 48px; height: 48px; object-fit: cover; border-radius: 8px; }
.sw-menu { position: absolute; left: 10px; right: 10px; bottom: 84px; z-index: 2; display: grid; gap: 2px; padding: 6px; border: 1px solid var(--ui-line-strong); border-radius: 12px; background: var(--ui-card); box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35); }
.sw-menu button { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border: 0; border-radius: 8px; background: transparent; color: var(--ui-ink); font-size: 13.5px; text-align: left; cursor: pointer; }
.sw-menu button:hover { background: var(--ui-pri-soft); }
.sw-menu small { margin-left: auto; color: var(--ui-dim); font-size: 11.5px; }
.sw-compose { display: flex; align-items: flex-end; gap: 6px; padding: 10px 12px 4px; }
.sw-input { flex: 1; min-height: 42px; max-height: 120px; padding: 10px 12px; border: 1px solid var(--ui-line-strong); border-radius: 12px; background: var(--ui-page); color: var(--ui-ink); font-size: 14px; line-height: 1.4; resize: none; }
.sw-input:focus { border-color: var(--ui-pri); outline: none; }
.sw-tool, .sw-send { display: grid; place-items: center; flex: none; width: 42px; height: 42px; border-radius: 12px; cursor: pointer; }
.sw-tool { border: 1px solid var(--ui-line-strong); background: var(--ui-page); color: var(--ui-ink); }
.sw-tool:hover { border-color: var(--ui-pri); }
.sw-send { border: 0; background: var(--ui-pri); color: #ffffff; }
.sw-send:disabled { opacity: 0.6; }
.sw-tool svg, .sw-send svg { width: 18px; height: 18px; }
.sw-hint { padding: 0 14px 10px; font-size: 11.5px; color: var(--ui-dim); }
.sw-drop { position: absolute; inset: 0; display: grid; place-items: center; border: 2px dashed var(--ui-pri); border-radius: 16px; background: rgba(43, 42, 63, 0.85); color: #ffffff; font-size: 15px; font-weight: 700; pointer-events: none; }

/* ---- phones: icon-only button, full-screen chat ---- */
@media (max-width: 600px) {
  .sw { right: 14px; bottom: 14px; }
  .sw-launch { width: 68px; height: 68px; padding: 0; justify-content: center; }
  .sw-launch__icon { width: 52px; height: 52px; }
  .sw-launch__icon svg { width: 30px; height: 30px; }
  .sw-dot { width: 16px; height: 16px; }
  .sw-preview { bottom: 80px; }
  .sw-launch__txt { display: none; }
  .sw-launch__icon { background: transparent; }
  .sw.is-open { inset: 0; right: 0; bottom: 0; }
  .sw-panel { width: 100vw; height: 100dvh; border: 0; border-radius: 0; }
  .sw-back { display: grid; }
  .sw-min { display: none; }
  .sw-hint { display: none; }
}
@media (prefers-reduced-motion: reduce) { .sw-launch, .sw-launch.has-unread { animation: none; transition: none; } }

.chat-lightbox { position: fixed; inset: 0; z-index: 3000; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.82); cursor: zoom-out; outline: none; }
.chat-lightbox img { max-width: 94vw; max-height: 92vh; border-radius: 8px; box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6); cursor: default; }
.chat-lightbox-close { position: absolute; top: 14px; right: 18px; border: 0; background: transparent; color: #ffffff; font-size: 40px; line-height: 1; cursor: pointer; }
</style>

<style scoped>
.sw-known { margin: 0 0 6px; padding: 8px 10px; border: 1px solid #8a3a3f; border-radius: 10px; background: #4a2a2f; color: #f6c9c9; font-size: 12.5px; line-height: 1.4; }

/* ---- replies, reactions, edits ---- */
.sw-msg { position: relative; }
.sw-quote { display: flex; flex-direction: column; margin: 0 0 6px; padding: 3px 8px; border-left: 3px solid rgba(90, 85, 232, .7); border-radius: 6px; background: rgba(90, 85, 232, .08); font-size: 12px; }
.sw-quote b { font-size: 11px; opacity: .85; } .sw-quote span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 240px; }
.sw-deleted { font-style: italic; opacity: .6; }
.sw-text a { text-decoration: underline; color: inherit; }
.sw-time i { font-style: normal; opacity: .8; }
.sw-react-btn { position: absolute; top: -8px; right: -6px; width: 22px; height: 22px; border: 1px solid var(--ui-line-strong, #d9d8e6); border-radius: 50%; background: var(--ui-card, #fff); color: var(--ui-dim, #6b7280); font-size: 13px; line-height: 1; cursor: pointer; opacity: 0; transition: opacity .15s; }
.sw-msg.is-them .sw-react-btn { right: auto; left: -6px; }
.sw-msg:hover .sw-react-btn, .sw-msg.has-picker .sw-react-btn { opacity: 1; }
@media (hover: none) { .sw-react-btn { opacity: .7; } }
.sw-picker-pop { position: absolute; z-index: 5; top: -40px; right: 0; display: flex; gap: 2px; padding: 4px 6px; border: 1px solid var(--ui-line-strong, #d9d8e6); border-radius: 999px; background: var(--ui-card, #fff); box-shadow: 0 6px 18px rgba(0,0,0,.15); }
.sw-msg.is-them .sw-picker-pop { right: auto; left: 0; }
.sw-picker-pop button { border: 0; background: transparent; font-size: 18px; line-height: 1; padding: 2px 3px; cursor: pointer; border-radius: 8px; }
.sw-picker-pop button:hover { transform: scale(1.2); }
.sw-reactions { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 5px; }
.sw-chip-react { display: inline-flex; align-items: center; gap: 3px; border: 1px solid rgba(0,0,0,.12); background: rgba(255,255,255,.75); color: #111; border-radius: 999px; padding: 1px 7px; font-size: 13px; cursor: pointer; }
.sw-chip-react.is-mine { border-color: rgba(90, 85, 232, .8); background: rgba(90, 85, 232, .15); }
.sw-chip-react small { font-size: 11px; opacity: .8; } .sw-chip-react em { font-style: normal; font-size: 11px; opacity: .85; }
</style>
